import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Truck } from "@phosphor-icons/react";

/**
 * Reusable dispatch dialog.
 * Props:
 *   - order: the sale/order object (needs id, order_no, dispatch?.expected_delivery)
 *   - open, onOpenChange
 *   - onDispatched(updatedOrder) — called after successful dispatch
 */
export default function DispatchDialog({ order, open, onOpenChange, onDispatched }) {
    const [courier, setCourier] = useState("");
    const [tracking, setTracking] = useState("");
    const [expected, setExpected] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && order) {
            setCourier(order.dispatch?.courier || "");
            setTracking(order.dispatch?.tracking_no || "");
            const ed = order.dispatch?.expected_delivery;
            setExpected(ed ? new Date(ed).toISOString().slice(0, 10) : "");
            setNotes("");
            setSaving(false);
        }
    }, [open, order]);

    if (!order) return null;

    const submit = async () => {
        setSaving(true);
        try {
            const payload = {
                stage: "dispatched",
                courier: courier || null,
                tracking_no: tracking || null,
                expected_delivery: expected ? new Date(expected).toISOString() : null,
                notes: notes || "",
            };
            const { data } = await api.patch(`/sales/${order.id}/fulfillment`, payload);
            toast.success(`Order ${order.order_no} dispatched!`);
            onOpenChange(false);
            onDispatched && onDispatched(data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="dispatch-dialog">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-extrabold font-display tracking-tight flex items-center gap-2">
                        <Truck size={22} weight="duotone" style={{ color: "#15803D" }} />
                        Dispatch Order · <span className="font-mono">{order.order_no}</span>
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Courier / Transport</Label>
                        <Input value={courier} onChange={(e) => setCourier(e.target.value)}
                               placeholder="e.g. Delhivery, Own Vehicle, DTDC" className="mt-1.5 h-11" data-testid="dispatch-courier" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Tracking / AWB No.</Label>
                        <Input value={tracking} onChange={(e) => setTracking(e.target.value)}
                               className="mt-1.5 h-11 font-mono" data-testid="dispatch-tracking" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Expected Delivery Date</Label>
                        <Input type="date" value={expected} onChange={(e) => setExpected(e.target.value)}
                               className="mt-1.5 h-11" data-testid="dispatch-expected" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Notes (optional)</Label>
                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                                  rows={2} className="mt-1.5" placeholder="Any dispatch remarks..." data-testid="dispatch-notes" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dispatch-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={saving} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="dispatch-confirm">
                        {saving ? "Dispatching..." : "Confirm Dispatch"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
